<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" hide-details clearable />
            </template>

            <template #default>
              <v-row>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.language_id"
                    :disabled="isLoading"
                    :items="filterLanguageOptions"
                    label="Language"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.site_id"
                    :disabled="isLoading"
                    :items="filterSiteOptions"
                    label="Site"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.wp_publish_status"
                    :disabled="isLoading"
                    :items="publishStatusOptions"
                    label="Publish Status"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.author_id"
                    :disabled="isLoading"
                    :items="authors"
                    custom-type="avatar"
                    label="Author"
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col sm="4">
                  <datetime-picker
                    v-model="query.params.published_atGreaterThanOrEqualTo"
                    :disabled="isLoading"
                    :text-field-props="{ class: 'pt-0' }"
                    label="Published From"
                    clearable
                    hide-details
                    dense
                  />
                </v-col>
                <v-col sm="4">
                  <datetime-picker
                    v-model="query.params.published_atLessThanOrEqualTo"
                    :disabled="isLoading"
                    :text-field-props="{ class: 'pt-0' }"
                    label="Published To"
                    clearable
                    hide-details
                    dense
                  />
                </v-col>
                <v-col v-if="query.params.news_article_id" sm="4">
                  <text-input
                    v-model="query.params.news_article_id"
                    label="News Article ID"
                    clearable
                    hide-details
                    dense
                  />
                </v-col>
              </v-row>
            </template>
            <template #content>
              <news-article-table
                :articles="articles"
                :items-per-page="pagination.perPage"
                :page="pagination.currentPage"
                :server-items-length="pagination.total"
                :footer-props="{ 'items-per-page-options': perPageOptions }"
                @filter-by-id="filterByArticleId"
                @pagination="$_queryHandler_onUpdatePagination"
              />
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <fab-create v-if="isPermittedTo('create')" type="News Article" @click="showCreateModal = true" />
    <news-article-create-modal v-model="showCreateModal" />
    <generic-modal v-model="showPageTips" :hide-buttons="true" :hide-louis="true" title="News Article Tips">
      <v-card outlined>
        <v-card-title>Translated Articles</v-card-title>
        <v-card-text>
          <p>
            Articles that have been translated are grouped together. To add a translation for an article, navigate to
            the EDIT view for the article you want to translate and add a new language under the LANGUAGES tab.
          </p>
          <p>
            You'll then be able to find all language versions of an article by clicking the languages
            <v-icon small>language</v-icon> button on a news item.
          </p>
        </v-card-text>
      </v-card>
    </generic-modal>
  </page-loader>
</template>

<script>
// Services
import { authors, languages, news, sites } from '@/services/offers'

// Helpers
import { mapMutations, mapState, mapGetters } from 'vuex'
import isSet from '@/utils/is-set'

// Mixins
import QueryParamsHandler from '@/mixins/query-params-handler'
import PermissionsHandler from '@/mixins/permissions-handler'

// Components
import DatetimePicker from '@/components/datetime-picker'
import FabCreate from '@/components/fab-create'
import FilterAccordion from '@/components/filter-accordion'
import GenericModal from '@/components/modals/generic-modal'
import NewsArticleCreateModal from './components/news-article-create-modal'
import NewsArticleTable from './components/news-article-table'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'
import TextInput from '@/components/forms/text-input'

export default {
  name: 'NewsArticle',
  components: {
    DatetimePicker,
    FabCreate,
    FilterAccordion,
    GenericModal,
    NewsArticleCreateModal,
    NewsArticleTable,
    PageLoader,
    SearchInput,
    SelectList,
    TextInput,
  },
  mixins: [QueryParamsHandler, PermissionsHandler],
  data: () => ({
    articles: [],
    authors: [],
    categoryOptionSites: [],
    isLoading: false,
    languages: [],
    pagination: { perPage: 15 },
    perPageOptions: [15, 30, 50, 100],
    queriesDefined: [
      'author_id',
      'site_id',
      'language_id',
      'site_id',
      'wp_publish_status',
      'news_article_id',
      'published_atGreaterThanOrEqualTo',
      'published_atLessThanOrEqualTo',
      'created_atGreaterThanOrEqualTo',
    ],
    query: {
      params: {
        site_id: undefined,
        language_id: undefined,
        site_id: undefined,
        wp_publish_status: undefined,
        news_article_id: undefined,
        published_atGreaterThanOrEqualTo: undefined,
        published_atLessThanOrEqualTo: undefined,
      },
      searchTerm: undefined,
    },
    queryTypes: {
      site_id: 'number',
      language_id: 'number',
      site_id: 'number',
      news_article_id: 'number',
      published_atGreaterThanOrEqualTo: 'date',
      published_atLessThanOrEqualTo: 'date',
    },
    showCreateModal: false,
    showPageTips: false,
  }),
  computed: {
    ...mapState('newsArticles', ['requiredCategorisedSchema']),
    ...mapGetters({ getItemsForOption: 'options/getItemsForOption' }),
    publishStatusOptions() {
      return this.getItemsForOption('wordpressStatuses')
    },
    filterSiteOptions() {
      if (!this.categoryOptionSites.length) return []
      return this.categoryOptionSites
    },
    filterLanguageOptions() {
      if (!this.languages.length) return []
      return this.languages.map(({ name, id }) => ({ key: name, value: id }))
    },
    showClearFilters() {
      return this.queriesDefined.some(filter => isSet(this.query.params[filter])) || this.query.searchTerm
    },
  },
  watch: {
    'query.params.news_article_id'(value) {
      if (value === null || value === '') {
        this.query.params.news_article_id = undefined
      }
    },
  },
  async created() {
    await Promise.all([this.getArticles(), this.fetchAuthors()])
    this.getCategoryOptionSites()
    this.getLanguages()
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations({
      setPageLoading: 'general/setPageLoading',
    }),
    async fetchAuthors() {
      this.isLoading = true
      try {
        const { data } = await authors.getAll({ paginate: { perPage: null } })
        this.authors = data.map(({ first_name, last_name, id, profile_picture }) => ({
          key: `${first_name} ${last_name}`,
          value: id,
          icon: profile_picture,
        }))
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch authors.',
          data: { error },
        })
      }
      this.isLoading = false
    },
    filterByArticleId(id) {
      this.$set(this.query.params, 'news_article_id', id)
    },
    async getArticles() {
      this.isLoading = true
      const { params, searchTerm: search } = this.query
      const { perPage, currentPage: page } = this.pagination
      const filters = Object.entries(params).reduce((acc, [key, value]) => {
        if (isSet(value)) acc[key] = value
        return acc
      }, {})

      try {
        const { data, pagination } = await news.getAll({ filters, search, perPage, page })
        this.pagination = pagination
        this.articles = data
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve news articles',
        })
      }
      this.isLoading = false
    },
    async getCategoryOptionSites() {
      try {
        const { data } = await sites.getAllSites({ filters: { type: 'organic' } })
        this.categoryOptionSites = data.map(({ name, id }) => ({ key: name, value: id }))
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve sites options',
        })
      }
    },
    async getLanguages() {
      try {
        this.languages = await languages.getLanguages()
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve language options',
        })
      }
    },
    handleQueryUpdate() {
      this.getArticles()
    },
    onClearFilters() {
      this.queriesDefined.forEach(filter => {
        this.$set(this.query.params, filter, null)
      })
      this.query.searchTerm = null
    },
  },
}
</script>
